import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Upload, ExternalLink, Sparkles, Loader2, ArrowLeft, ImageIcon } from "lucide-react";

const shrink = (dataUrl) =>
  new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const maxW = 1280;
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      res(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => res(dataUrl);
    img.src = dataUrl;
  });

const fileToDataUrl = (file) =>
  new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });

export default function ImagePickerModal({ open, onOpenChange, onSelect }) {
  const [view, setView] = useState("menu");
  const [query, setQuery] = useState("");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const uploadRef = useRef();
  const unsplashUploadRef = useRef();
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
    if (open) { setView("menu"); setQuery(""); setPrompt(""); }
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    const small = await shrink(dataUrl);
    onSelect(small);
    onOpenChange(false);
    toast.success("Изображението е добавено");
  };

  const openUnsplash = () => {
    const url = query.trim()
      ? `https://unsplash.com/s/photos/${encodeURIComponent(query.trim())}`
      : "https://unsplash.com";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const generateAi = async () => {
    if (!prompt.trim()) return toast.error("Опишете какво изображение искате");
    setGenerating(true);
    try {
      const { data } = await api.post("/generate-image", { prompt: prompt.trim() });
      if (!openRef.current) return;
      const small = await shrink(data.image);
      onSelect(small);
      onOpenChange(false);
      toast.success("Изображението е генерирано и добавено");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const cards = [
    { id: "upload", label: "Качи от устройство", desc: "Снимка от компютъра или телефона", icon: Upload, color: "sky", onClick: () => uploadRef.current?.click() },
    { id: "unsplash", label: "Избери от Unsplash", desc: "Отвори Unsplash в нов раздел", icon: ExternalLink, color: "emerald", onClick: () => setView("unsplash") },
    { id: "ai", label: "Генерирай с AI", desc: "Опиши и AI ще нарисува", icon: Sparkles, color: "amber", onClick: () => setView("ai") },
  ];
  const tone = {
    sky: "bg-sky-100 text-sky-600 group-hover:bg-sky-500 group-hover:text-white",
    emerald: "bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white",
    amber: "bg-amber-100 text-amber-600 group-hover:bg-amber-500 group-hover:text-white",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl p-8">
        <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="picker-upload-input" />
        <input ref={unsplashUploadRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="picker-unsplash-upload-input" />

        <div className="flex items-center gap-3 mb-6">
          {view !== "menu" && (
            <button data-testid="picker-back-btn" onClick={() => setView("menu")} className="p-2 rounded-full hover:bg-slate-100 transition-colors">
              <ArrowLeft size={18} />
            </button>
          )}
          <DialogTitle className="font-heading text-2xl font-black text-slate-900">Добави изображение</DialogTitle>
        </div>
        <DialogDescription className="sr-only">Изберете начин за добавяне на изображение към слайда: качване, Unsplash или генериране с AI.</DialogDescription>

        <AnimatePresence mode="wait">
          {view === "menu" && (
            <motion.div key="menu" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="grid sm:grid-cols-3 gap-4">
              {cards.map((c, i) => (
                <motion.button key={c.id} data-testid={`picker-${c.id}`} onClick={c.onClick}
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * i }}
                  className="group bg-white border border-slate-100 rounded-2xl p-6 text-center shadow-[0_8px_30px_rgb(12,165,233,0.05)] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] flex flex-col items-center">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-colors ${tone[c.color]}`}>
                    <c.icon size={30} />
                  </div>
                  <div className="font-heading font-bold text-slate-900">{c.label}</div>
                  <div className="text-xs text-slate-400 mt-1 leading-relaxed">{c.desc}</div>
                </motion.button>
              ))}
            </motion.div>
          )}

          {view === "unsplash" && (
            <motion.div key="unsplash" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <p className="text-slate-500 text-sm">Потърси снимка в Unsplash, изтегли я, след което я качи тук.</p>
              <div className="flex gap-2">
                <input data-testid="unsplash-query" value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && openUnsplash()}
                  placeholder="напр. слънчева система, животни, природа..."
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-300 outline-none" />
                <button data-testid="open-unsplash-btn" onClick={openUnsplash}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 rounded-xl flex items-center gap-2 transition-colors whitespace-nowrap">
                  <ExternalLink size={16} /> Отвори Unsplash
                </button>
              </div>
              <button data-testid="unsplash-then-upload-btn" onClick={() => unsplashUploadRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 font-medium hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors">
                <Upload size={16} className="text-emerald-500" /> Качи изтеглената снимка
              </button>
              <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
                <ImageIcon size={12} /> Изображенията се предоставят от Unsplash.
              </p>
            </motion.div>
          )}

          {view === "ai" && (
            <motion.div key="ai" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <p className="text-slate-500 text-sm">Опиши изображението, което искаш, и AI ще го създаде за твоя слайд.</p>
              <textarea data-testid="ai-prompt" rows="3" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="напр. цветна илюстрация на слънчевата система с планетите..."
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-300 outline-none resize-none" />
              <button data-testid="generate-ai-image-btn" onClick={generateAi} disabled={generating}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {generating ? <><Loader2 size={20} className="animate-spin" /> AI рисува...</> : <><Sparkles size={20} /> Генерирай</>}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
